export const BOOKING_ROOM_IMAGE_RATIO = Object.freeze({
  width: 3,
  height: 2,
  tolerancePx: 2,
});

function asPositiveNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : 0;
}

/**
 * Room imagery is displayed in a fixed 3:2 landscape frame. A two-pixel
 * tolerance accommodates integer rounding and metadata normalisation without
 * accepting visibly different aspect ratios.
 */
export function validateBookingRoomImageDimensions(
  width,
  height,
  { tolerancePx = BOOKING_ROOM_IMAGE_RATIO.tolerancePx } = {}
) {
  const naturalWidth = asPositiveNumber(width);
  const naturalHeight = asPositiveNumber(height);
  const safeTolerance = Math.max(0, asPositiveNumber(tolerancePx));

  if (!naturalWidth || !naturalHeight) {
    return {
      valid: false,
      reason: "unreadable",
      width: naturalWidth,
      height: naturalHeight,
      differencePx: null,
    };
  }

  const expectedWidth =
    (naturalHeight * BOOKING_ROOM_IMAGE_RATIO.width) /
    BOOKING_ROOM_IMAGE_RATIO.height;
  const differencePx = Math.abs(naturalWidth - expectedWidth);
  const valid = naturalWidth > naturalHeight && differencePx <= safeTolerance;

  return {
    valid,
    reason: valid ? null : "ratio",
    width: naturalWidth,
    height: naturalHeight,
    differencePx,
  };
}

export function readBookingRoomImageDimensions(
  file,
  {
    ImageConstructor = typeof Image === "undefined" ? null : Image,
    urlApi = typeof URL === "undefined" ? null : URL,
  } = {}
) {
  if (!file || !ImageConstructor || !urlApi?.createObjectURL) {
    return Promise.reject(new Error("IMAGE_DIMENSIONS_UNAVAILABLE"));
  }

  return new Promise((resolve, reject) => {
    let objectUrl = "";
    const image = new ImageConstructor();

    const cleanup = () => {
      image.onload = null;
      image.onerror = null;
      if (objectUrl && typeof urlApi.revokeObjectURL === "function") {
        urlApi.revokeObjectURL(objectUrl);
      }
    };

    image.onload = () => {
      const width = image.naturalWidth || image.width;
      const height = image.naturalHeight || image.height;
      cleanup();

      if (!width || !height) {
        reject(new Error("IMAGE_DIMENSIONS_UNAVAILABLE"));
        return;
      }

      resolve({ width, height });
    };

    image.onerror = () => {
      cleanup();
      reject(new Error("IMAGE_COULD_NOT_BE_READ"));
    };

    try {
      objectUrl = urlApi.createObjectURL(file);
      image.src = objectUrl;
    } catch (error) {
      cleanup();
      reject(error);
    }
  });
}

export async function validateBookingRoomImageFile(file, options = {}) {
  const dimensions = await readBookingRoomImageDimensions(file, options);
  return validateBookingRoomImageDimensions(
    dimensions.width,
    dimensions.height,
    options
  );
}
